import React, { useState, useEffect } from 'react';
import { 
    Dialog, 
    DialogContent, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    TextField, 
    Button 
} from '@material-ui/core';
import { RichTextEditor } from './RichTextEditor';
import { formatDateTimeLocal, toApiDateTime } from '../../utils/BannerUtils';

const pageOptions = [
    "All Pages",
];

export const AddBannerDialog = ({
    open,
    onClose,
    onSubmit,
    banner,
    mode,
}) => {
    const [type, setType] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [pages, setPages] = useState(['All Pages']);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [dateError, setDateError] = useState('');

    const isEditMode = mode === 'edit';
    
    useEffect(() => {
        if( open && isEditMode && banner) {
            setType(banner.type);
            setStartDate(formatDateTimeLocal(banner.startDate));
            setEndDate(formatDateTimeLocal(banner.endDate));
            setPages(banner.paths?.length ? banner.paths : ['All Pages']);
            setMessage(banner.message);
        }
        else if( open && !isEditMode) {
            resetForm();
        }
    }, [banner, open, isEditMode]);

    const resetForm = () => {
        setType('');
        setStartDate('');
        setEndDate('');
        setPages(['All Pages']);
        setMessage('');
        setError('');
        setDateError('');
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = () => {
        if (!type || !startDate || !endDate || !pages || !message) {
            setError('Please fill in all required fields');
            return;
        }
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (start >= end) {
            setDateError('End Date must be later than Start Date');
            return;
        }
        setDateError('');
        onSubmit({
            _id: banner?._id,
            type,
            startDate: toApiDateTime(startDate),
            endDate: toApiDateTime(endDate),
            paths: pages,
            message: message?.trim()
        });
        resetForm();
    };

    return (
        <Dialog open={open} 
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                className: 'add-banner-dialog',
            }}
        >
            <DialogContent className='add-banner-dialog-content'>
                <div className='add-banner-form'>
                    <FormControl 
                        required
                        fullWidth
                        className='banner-form-field'
                    >
                        <InputLabel>Type</InputLabel>
                        <Select value={type} onChange={(e) => setType(e.target.value as string)}>
                            <MenuItem value="notice">Notice</MenuItem>
                            <MenuItem value="info">Info</MenuItem>
                            <MenuItem value="warning">Warning</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField 
                        required
                        fullWidth
                        className='banner-form-field'
                        label='Start Date'
                        type='datetime-local'
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value as string)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <TextField 
                        required
                        fullWidth
                        className='banner-form-field'
                        label='End Date'
                        type='datetime-local'
                        value={endDate}
                        error={!!dateError}
                        helperText={dateError}
                        onChange={(e) => {
                            let value = e.target.value as string;
                            setEndDate(value);
                            setDateError('');
                            if(startDate &&value) {
                                const start = new Date(startDate);
                                const end = new Date(value);
                                if(end < start) {
                                    setDateError('End Date must be later than Start Date');
                                    return;
                                }
                            }
                        }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <FormControl 
                        required
                        fullWidth
                        className='banner-form-field'
                    >
                        <InputLabel>Pages</InputLabel>
                        <Select 
                            value={pages} 
                            onChange={(e) => {
                                const selectedPages = e.target.value as string[];
                                if (!selectedPages.includes('All Pages')) {
                                    setPages(['All Pages']);
                                    return;
                                }
                                setPages(selectedPages);
                            }} 
                            multiple
                            renderValue={(selected) => (selected as string[]).join(', ')}
                        >
                            {
                                pageOptions.map((page) => (
                                    <MenuItem key={page} value={page}>{page}</MenuItem>
                                ))
                            }
                        </Select>
                        <div className='banner-pages-helper'>
                            If no option is selected, banner will appear on all pages
                        </div>
                    </FormControl>
                    <RichTextEditor
                        value={message}
                        onChange={(value) => setMessage(value)}
                    />
                    {
                        error && (
                            <div className='banner-form-error'>
                                {error}
                            </div>
                        )
                    }
                    <div className='add-banner-form-actions'>
                        <Button variant='contained' className='banner-submit-button' onClick={handleSubmit}>{isEditMode ? "Update" : "Submit"}</Button>
                        <Button variant='contained' className='banner-cancel-button' onClick={handleClose}>Cancel</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export const DeleteBannerDialog = ({
    open,
    onClose,
    onSubmit,
}) => {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogContent className='delete-banner-dialog-content'>
                <div className='delete-banner-form'>
                    <div className='banners-title'>Delete Banner</div>
                    <p className='delete-banner-description'>Are you sure you want to delete this banner?</p>
                    <div className='delete-banner-actions'>
                        <Button variant='contained' className='banner-submit-button' onClick={onSubmit}>Submit</Button>
                        <Button variant='contained' className='banner-cancel-button' onClick={onClose}>Cancel</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};